<template>
  <div>
    <breadcrumb-header
      v-if="showBreadcrumbs"
      :items="breadcrumbs"
    />
    <v-expansion-panels
      v-if="location"
      v-model="panels"
      multiple
    >
      <v-expansion-panel
        color="primary"
        value="details"
      >
        <template #title>
          <div class="text-headline-small text-white">
            {{ t('location.info') }}
          </div>
        </template>
        <template #text>
          <rich-text-content
            :data="location.details"
          />
          <location-marker-editor
            v-if="showMap"
            v-model="location"
            class="mt-2"
            :store="location?.store"
            read-only
          />
        </template>
      </v-expansion-panel>
      <v-expansion-panel
        v-if="location.languages?.length"
        color="primary"
        value="languages"
      >
        <template #title>
          <div class="text-headline-small text-white">
            {{ t('language.title', 2) }}
          </div>
        </template>
        <template #text>
          <v-chip
            v-for="language in location.languages"
            :key="language.id"
            :text="language.descr ?? language.name"
            class="me-2 mt-2"
          />
        </template>
      </v-expansion-panel>
      <v-expansion-panel
        v-if="location.active_carts?.length"
        color="primary"
        value="carts"
      >
        <template #title>
          <div class="text-headline-small text-white">
            {{ t('location.carts_in_use') }}
          </div>
        </template>
        <template #text>
          <cart-list :carts="location.active_carts" />
        </template>
      </v-expansion-panel>
      <v-expansion-panel
        v-if="location?.images?.length"
        color="primary"
        value="gallery"
      >
        <template #title>
          <div class="text-headline-small text-white">
            {{ t('images.gallery') }}
          </div>
        </template>

        <template #text>
          <image-gallery
            v-model="location.images"
            name="location_gallery"
            enable-slide-show
            read-only
          />
        </template>
      </v-expansion-panel>
      <v-expansion-panel
        v-if="shiftId"
        color="primary"
        value="contacts"
      >
        <template #title>
          <div class="text-headline-small text-white">
            {{ t('store.contact', 2) }}
          </div>
        </template>
        <template #text>
          <v-alert
            v-if="!storeContacts || (storeContacts.length === 0)"
            type="info"
            :text="t('store.no_contacts_available')"
            class="mt-2"
          />
          <v-container v-else>
            <v-row>
              <v-col
                v-for="(captainLocation, index) in storeContacts"
                :key="index"
                cols="12"
                sm="6"
                md="4"
              >
                <member-card
                  class="mt-3"
                  :user="captainLocation.captain"
                  :title="captainLocation.location.name"
                  :show-email="false"
                  fill-height
                />
              </v-col>
            </v-row>
          </v-container>
        </template>
      </v-expansion-panel>
    </v-expansion-panels>
  </div>
</template>

<script setup lang="ts">
import axios from 'axios';
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useSettings } from '@/composables';
import { useErrorStore } from '@/stores/errors.ts';
import CartList from '../carts/CartList.vue';
import ImageGallery from '../images/ImageGallery.vue';
import RichTextContent from '../rich-text/RichTextContent.vue';
import MemberCard from '../teams/MemberCard.vue';
import LocationMarkerEditor from './LocationMarkerEditor.vue';

const props = withDefaults(defineProps<{
  locationId: number | string;
  shiftId?: number;
  showBreadcrumbs?: boolean;
  showMap?: boolean;
}>(), {
  showBreadcrumbs: () => true,
  showMap: () => true,
});
const { getSetting } = useSettings();
const panels = ref<string[]>([]);
if (!getSetting('enable_cost_savings')) {
  panels.value.push('details');
}
const { t } = useI18n();

const { handleError } = useErrorStore();

const location = ref<App.Models.Location>();

const storeContacts = ref<any>([]);
async function loadData() {
  try {
    const locationResponse = await axios.get(`/api/locations/${props.locationId}`, {
      params: {
        inc: [
          'images',
          'markers',
          'markers.images',
          'heroImage',
          'store',
          'store.images',
          'languages',
          'activeCarts',
        ],
      },
    });
    location.value = locationResponse.data.data;
    if (props.shiftId) {
      const shiftResponse = await axios.get(`/api/users/shifts/${props.shiftId}/stores`);
      storeContacts.value = shiftResponse.data.data;
    }
  }
  catch (error) {
    handleError(error, t('errors.cannot_load'));
  }
}

onMounted(() => {
  loadData();
});

const breadcrumbs = computed(() => [
  {
    title: t('location.title'),
    to: '/locations',
  },
  {
    title: location.value?.name ?? t('states.loading'),
  },
]);
</script>
